import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaWhatsapp } from 'react-icons/fa';
import { HiX } from 'react-icons/hi';
import { COMPANY_INFO } from '../utils/constants';

const FloatingWhatsApp = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    // Show the button after 2 seconds
    const timer = setTimeout(() => {
      setIsVisible(true);
      
      // Show tooltip briefly after 3 more seconds
      setTimeout(() => {
        setShowTooltip(true);
        setTimeout(() => setShowTooltip(false), 4000);
      }, 3000);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleClick = () => {
    // TODO: Replace with real WhatsApp number
    window.open(COMPANY_INFO.whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Floating WhatsApp Button */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ 
          type: "spring",
          stiffness: 500,
          damping: 30,
          duration: 0.6 
        }}
        className="fixed bottom-6 right-6 z-50"
      >
        {/* Tooltip */}
        <AnimatePresence>
          {showTooltip && !isExpanded && (
            <motion.div
              initial={{ opacity: 0, x: 20, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 20, scale: 0.8 }}
              transition={{ duration: 0.3 }}
              className="absolute right-full top-1/2 transform -translate-y-1/2 mr-4"
            >
              <div className="bg-gray-900 text-white px-4 py-2 rounded-lg shadow-xl border border-gray-700 whitespace-nowrap">
                <span className="text-sm font-medium">Need help? Chat with us!</span>
                <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900 border-r border-b border-gray-700"></div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Expanded Chat Widget */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ duration: 0.3 }}
              className="absolute bottom-full right-0 mb-4 w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
            >
              {/* Header */}
              <div className="bg-green-500 text-white p-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <FaWhatsapp className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-semibold">Rishant Technologies</div>
                    <div className="text-sm opacity-90">Typically replies instantly</div>
                  </div>
                </div>
                <button
                  onClick={toggleExpanded}
                  className="p-1 hover:bg-white/20 rounded-full transition-colors duration-200"
                >
                  <HiX className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 bg-gray-50">
                <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                  <p className="text-gray-800 text-sm leading-relaxed">
                    👋 Hi there! Ready to automate your business processes? 
                  </p>
                  <p className="text-gray-600 text-sm mt-2">
                    Click below to start a conversation with us on WhatsApp.
                  </p>
                </div>

                <button
                  onClick={handleClick}
                  className="w-full bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <FaWhatsapp className="w-5 h-5" />
                  <span>Start Conversation</span>
                </button>

                <div className="text-center mt-3">
                  <span className="text-xs text-gray-500">
                    We reply within 4 business hours
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Button */}
        <motion.button
          onClick={isExpanded ? handleClick : toggleExpanded}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="relative w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-2xl flex items-center justify-center transition-colors duration-300 group"
        >
          {/* Pulse Animation Ring */}
          <motion.div
            animate={{ scale: [1, 1.3, 1], opacity: [0.7, 0, 0.7] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-0 bg-green-400 rounded-full"
          />
          
          {/* Icon */}
          <FaWhatsapp className="w-7 h-7 relative z-10" />
          
          {/* Hover Text */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileHover={{ opacity: 1, scale: 1 }}
            className="absolute right-full top-1/2 transform -translate-y-1/2 mr-3 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap pointer-events-none"
          >
            Chat with us
            <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
          </motion.div>
        </motion.button>

        {/* Notification Dot */}
        <motion.div
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold"
        >
          1
        </motion.div>
      </motion.div>
    </>
  );
};

export default FloatingWhatsApp;